import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import { buildTabPath, resolveTabSlug } from '../shared/surface/tabPath';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import { getRecommendedLawyers } from '../../store/actions/Finders/FIlnderActions';
import { fetchMatterSummary } from '../../store/actions/Matters/MatterActions';
import Badge from '../shared/TailwindComponents/Badge';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Button from '../shared/TailwindComponents/Button';
import { PiBriefcaseLight, PiHandshakeLight, PiFileTextLight, PiCaretLeftLight, PiCurrencyDollarLight, PiClockLight, PiChatCircleTextLight, PiCalendarLight, PiUserLight } from 'react-icons/pi';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import GlassCard from '../shared/brand/GlassCard';
import apis from '../../utils/apis';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';

interface LawyerRecommendation {
  lawyer_id: string;
  first_name: string;
  last_name: string;
  specialty: string[];
  experience: number;
  hourly_rate: number;
  location: string[] | string;
  bio: string;
  ratings_average: number;
  profilePicture?: string;
  reason: string;
}

interface Proposal {
  id: string;
  lawyer_id: string;
  client_id: string;
  case_id: string;
  budget: number;
  timeframe: string;
  message: string;
  status: string;
  created_at: string;
  updated_at: string;
  offer?: Offer;
}

interface Offer {
  totalPrice: number;
  plannedHours: number;
  pricePerHour: number;
  timeframe: string;
  expectedOutcome: string;
  paymentTerms: {
    methods: string[];
    retainer: number;
    intervals: string;
  };
  scopeOfWork: string;
  expertise: string;
  communicationPlan: string;
  risks: string;
  callToAction: string;
}

const WaitingForEngagementTerms: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="p-4 bg-warning-100 dark:bg-warning-900 rounded-lg mb-4">
      <p className="text-warning-800 dark:text-warning-200">
        {t('Waiting for the lawyer to generate engagement terms for both parties to sign.')}
      </p>
    </div>
  );
};

const ProposalForm: React.FC<{ lawyerId: string; caseId: string }> = ({ lawyerId, caseId }) => {
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: {
      budget: '',
      timeframe: '',
      message: '',
    },
    validationSchema: Yup.object().shape({
      budget: Yup.number()
        .required(t('Budget is required'))
        .positive(t('Budget must be positive')),
      timeframe: Yup.string().required(t('Timeframe is required')),
      message: Yup.string().required(t('Message is required')),
    }),
    onSubmit: async (values) => {
      formik.resetForm();
    },
  });

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
      <h2 className="text-xl font-medium mb-4 text-gray-900 dark:text-gray-100">{t('Submit a proposal')}</h2>
      <div className="space-y-6">
        <Input
          label={t('Budget')}
          type="number"
          name="budget"
          value={formik.values.budget}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t('Enter your budget')}
          error={formik.touched.budget && formik.errors.budget ? formik.errors.budget : undefined}
          required
        />
        <Input
          label={t('Timeframe')}
          type="text"
          name="timeframe"
          value={formik.values.timeframe}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t('e.g., 2 weeks')}
          error={formik.touched.timeframe && formik.errors.timeframe ? formik.errors.timeframe : undefined}
          required
        />
        <Textarea
          label={t('Message')}
          name="message"
          value={formik.values.message}
          onChange={formik.handleChange}
          placeholder={t('Enter your message to the lawyer')}
          error={formik.touched.message && formik.errors.message ? formik.errors.message : undefined}
          id={''}
        />
        <div className="flex justify-end space-x-4">
          <Button size="small" variant="neutral" onClick={() => formik.resetForm()}>
            {t('Cancel')}
          </Button>
          <Button size='small' variant="secondary" onClick={() => formik.handleSubmit()} disabled={formik.isSubmitting}>
            {formik.isSubmitting ? t('Submitting...') : t('Submit proposal')}
          </Button>
        </div>
      </div>
    </div>
  );
};

interface ProposalCardProps {
  proposal: Proposal;
  lawyer: LawyerRecommendation;
  caseName: string;
}

const ProposalCard: React.FC<ProposalCardProps> = ({ proposal, lawyer, caseName }) => {
  const { t } = useTranslation();

  const statusColors: Record<string, BadgeColor> = {
    Pending: 'yellow',
    Accepted: 'green',
    Rejected: 'red',
    Offered: 'blue',
  };

  return (
    <div className="glass hover:bg-gray-50 transition rounded-lg p-6 shadow-sm flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PiFileTextLight className="text-gray-500 dark:text-gray-400 h-6 w-6" />
          <h2 className="text-xl font-medium text-gray-900 dark:text-white">{t('Proposal details')}</h2>
        </div>
        <Badge color={statusColors[proposal.status] || 'gray'} className="text-sm">
          {t(proposal.status)}
        </Badge>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700 dark:text-gray-300">
        <div className="flex items-center gap-2">
          <PiUserLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Lawyer')}:</span>
          <span>{lawyer.first_name} {lawyer.last_name}</span>
        </div>
        <div className="flex items-center gap-2">
          <PiBriefcaseLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Case')}:</span>
          <span>{caseName}</span>
        </div>
        <div className="flex items-center gap-2">
          <PiCurrencyDollarLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Budget')}:</span>
          <span>${proposal.budget}</span>
        </div>
        <div className="flex items-center gap-2">
          <PiClockLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Timeframe')}:</span>
          <span>{proposal.timeframe}</span>
        </div>
      </div>
      <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
        <PiChatCircleTextLight className="text-gray-500 dark:text-gray-400 mt-1" />
        <div className="flex-1">
          <span className="font-medium block">{t('Message')}:</span>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{proposal.message}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <PiCalendarLight className="text-gray-500 dark:text-gray-400" />
        <span className="font-medium">{t('Submitted on')}:</span>
        <span>{new Date(proposal.created_at).toLocaleString()}</span>
      </div>
    </div>
  );
};

const OfferCard: React.FC<{ offer: Offer; lawyer: LawyerRecommendation; caseName: string; onRespond: (response: string, reason?: string) => void; status: string }> = ({ offer, lawyer, caseName, onRespond, status }) => {
  const { t } = useTranslation();
  const [declineReason, setDeclineReason] = useState('');

  const statusColors: Record<string, BadgeColor> = {
    Offered: 'blue',
    Accepted: 'green',
    Rejected: 'red',
  };

  return (
    <div className="glass hover:bg-gray-50 transition rounded-lg p-6 shadow-sm flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PiCurrencyDollarLight className="text-gray-500 dark:text-gray-400 h-6 w-6" />
          <h2 className="text-xl font-medium text-gray-900 dark:text-white">{t('Offer details')}</h2>
        </div>
        <Badge color={statusColors[status] || 'gray'} className="text-sm">{t(status)}</Badge>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700 dark:text-gray-300">
        <div className="flex items-center gap-2">
          <PiUserLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Lawyer')}:</span>
          <span>{lawyer.first_name} {lawyer.last_name}</span>
        </div>
        <div className="flex items-center gap-2">
          <PiBriefcaseLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Case')}:</span>
          <span>{caseName}</span>
        </div>
        <div className="flex items-center gap-2">
          <PiCurrencyDollarLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Total price')}:</span>
          <span>${offer.totalPrice}</span>
        </div>
        <div className="flex items-center gap-2">
          <PiClockLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Planned hours')}:</span>
          <span>{offer.plannedHours}</span>
        </div>
        <div className="flex items-center gap-2">
          <PiCurrencyDollarLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Price per hour')}:</span>
          <span>${offer.pricePerHour}</span>
        </div>
        <div className="flex items-center gap-2">
          <PiClockLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Timeframe')}:</span>
          <span>{offer.timeframe}</span>
        </div>
      </div>
      <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
        <PiChatCircleTextLight className="text-gray-500 dark:text-gray-400 mt-1" />
        <div className="flex-1">
          <span className="font-medium block">{t('Scope of work')}</span>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{offer.scopeOfWork}</p>
        </div>
      </div>
      <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
        <PiChatCircleTextLight className="text-gray-500 dark:text-gray-400 mt-1" />
        <div className="flex-1">
          <span className="font-medium block">{t('Expected outcome')}</span>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{offer.expectedOutcome}</p>
        </div>
      </div>
      <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
        <PiChatCircleTextLight className="text-gray-500 dark:text-gray-400 mt-1" />
        <div className="flex-1">
          <span className="font-medium block">{t('Communication plan')}</span>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{offer.communicationPlan}</p>
        </div>
      </div>
      <div className="mt-4">
        <span className="font-medium text-gray-700 dark:text-gray-300">{t('Payment terms')}:</span>
        <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 mt-1">
          <li>{t('Retainer')}: ${offer.paymentTerms.retainer}</li>
          <li>{t('Intervals')}: {offer.paymentTerms.intervals}</li>
          <li>{t('Methods')}: {offer.paymentTerms.methods.join(', ')} {t('Payment')}</li>
        </ul>
      </div>
      {status === 'Offered' && (
        <>
          <Textarea
            label={t('Decline reason (optional)')}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder={t('Provide a reason if declining the offer')}
            className="mt-4"
            id=""
          />
          <div className="flex justify-end gap-4 mt-4">
            <Button variant="primary" size='small' onClick={() => onRespond('accept')}>
              {t('Accept offer')}
            </Button>
            <Button variant="secondary" size='small' onClick={() => onRespond('decline', declineReason)}>
              {t('Decline offer')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const LAWYER_TABS = ['information', 'proposal', 'offer', 'engagement'] as const;
type LawyerTab = typeof LAWYER_TABS[number];

const LawyerDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { setBreadcrumbs } = useBreadcrumbs();
  /* Hydrate from the route - `/finder/:caseId/:lawyerId/:tab?` - and a fetch,
     NOT from `location.state`, so a refresh or a deep link still resolves the
     lawyer. The tab is a path segment too, so it survives the same way. */
  const { caseId, lawyerId, tab: tabParam } = useParams<{ caseId: string; lawyerId: string; tab?: string }>();
  const activeTab = resolveTabSlug(LAWYER_TABS, tabParam) as LawyerTab;
  const [lawyer, setLawyer] = useState<LawyerRecommendation | null>(null);
  const [caseName, setCaseName] = useState<string>(t('Unknown case'));
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const dispatch = useAppDispatch();
  const clientId = useSelector((state: RootState) => state.user.userInfo.UserID);
  const [offer, setOffer] = useState<Offer | null>(null);

  const goToTab = useCallback(
    (value: string) => history.push(buildTabPath(`/finder/${caseId}/${lawyerId}`, value)),
    [history, caseId, lawyerId]
  );

  // Load the lawyer (and the case name) from the route params.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const [recommended, summary] = await Promise.all([
          dispatch(getRecommendedLawyers(caseId)) as unknown as Promise<LawyerRecommendation[]>,
          dispatch(fetchMatterSummary(caseId)).catch(() => null),
        ]);
        if (!active) return;
        const found = Array.isArray(recommended)
          ? recommended.find((l) => String(l.lawyer_id) === String(lawyerId))
          : undefined;
        setLawyer(found || null);
        setNotFound(!found);
        const name = (summary as any)?.summary?.CaseName;
        if (name) setCaseName(name);
      } catch (error) {
        if (active) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [caseId, lawyerId, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const proposalResponse: any = {}
        if (proposalResponse && proposalResponse.proposal) {
          setProposal(proposalResponse.proposal);
          if (proposalResponse.proposal.offer) {
            setOffer(proposalResponse.proposal.offer);
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    if (lawyer && caseId && clientId) {
      fetchData();
    }
  }, [lawyer, caseId, clientId, dispatch]);

  const tabs = [
    { label: t('Information'), value: 'information', icon: <PiBriefcaseLight className="h-5 w-5 text-gray-500" /> },
    { label: t('Proposal'), value: 'proposal', icon: <PiFileTextLight className="h-5 w-5 text-gray-500" /> },
    { label: t('Offer'), value: 'offer', icon: <PiCurrencyDollarLight className="h-5 w-5 text-gray-500" /> },
    { label: t('Engagement terms'), value: 'engagement', icon: <PiHandshakeLight className="h-5 w-5 text-gray-500" /> },
  ];

  const tabLabels: Record<LawyerTab, string> = {
    information: t('Information'),
    proposal: t('Proposal'),
    offer: t('Offer'),
    engagement: t('Engagement terms'),
  };

  useEffect(() => {
    setBreadcrumbs([
      { name: t('Finder'), href: '/finder', current: false },
      { name: caseName, href: `/finder/${caseId}`, current: false },
      {
        name: lawyer ? `${lawyer.first_name} ${lawyer.last_name}` : t('Loading'),
        href: `/finder/${caseId}/${lawyerId}`,
        current: false,
      },
      { name: tabLabels[activeTab], href: `/finder/${caseId}/${lawyerId}/${activeTab}`, current: true },
    ]);
    return () => setBreadcrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lawyer, caseName, caseId, lawyerId, activeTab, setBreadcrumbs, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <Header />
        <div className="p-6">
          <Skeleton variant="detail" />
        </div>
      </div>
    );
  }

  if (!lawyer || notFound) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-6">
        <p className="text-danger-500">{t('Lawyer not found')}</p>
      </div>
    );
  }

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<span key={i} className="text-warning-400">★</span>);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(
          <span key={i} className="text-warning-400 relative">
            ★
            <span className="absolute start-0 text-gray-300">☆</span>
          </span>
        );
      } else {
        stars.push(<span key={i} className="text-gray-300">☆</span>);
      }
    }
    return stars;
  };

  const renderProfile = () => (
    <div className="flex flex-col md:flex-row items-start md:space-x-4 mb-8">
      <button
        onClick={() => history.goBack()}
        className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors me-4"
      >
        <PiCaretLeftLight className="h-6 w-6 rtl:-scale-x-100" />
      </button>
      <div className="flex-shrink-0 mb-4 md:mb-0">
        {lawyer.profilePicture ? (
          <img
            src={lawyer.profilePicture}
            alt={`${lawyer.first_name} ${lawyer.last_name}`}
            className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700 transition-transform duration-200 hover:scale-105"
          />
        ) : (
          <div className="w-32 h-32 md:w-40 md:h-40 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-4xl font-medium text-gray-600 dark:text-gray-400">
            {`${lawyer.first_name[0]}${lawyer.last_name[0]}`.toUpperCase()}
          </div>
        )}
      </div>
      <div className="flex-1">
        <h2 className="text-2xl md:text-3xl font-medium text-gray-800 dark:text-gray-100">
          {lawyer.first_name} {lawyer.last_name}
        </h2>
        <div className="mt-3 flex items-center space-x-2">
          {renderStars(lawyer.ratings_average)}
          <span className="text-gray-600 dark:text-gray-300 text-sm">
            ({lawyer.ratings_average.toFixed(1)})
          </span>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-700 dark:text-gray-300">{t('Experience')}:</span>
            <span className="text-gray-600 dark:text-gray-400">
              {lawyer.experience ? `${lawyer.experience} ${t('years')}` : t('Not specified')}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-700 dark:text-gray-300">{t('Rate')}:</span>
            <span className="text-gray-600 dark:text-gray-400">
              {lawyer.hourly_rate ? `$${lawyer.hourly_rate}/hour` : t('Not specified')}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-700 dark:text-gray-300">{t('Location')}:</span>
            <span className="text-gray-600 dark:text-gray-400">
              {Array.isArray(lawyer.location) ? lawyer.location.join(', ') : lawyer.location || t('Not specified')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderInformationTab = () => (
    <div className="space-y-6">
      <div className='flex gap-4'>
      {lawyer.bio && (
        <GlassCard icon={<PiUserLight />} title={t('About')}>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{lawyer.bio}</p>
        </GlassCard>
      )}
      {lawyer.reason && (
        <GlassCard icon={<PiChatCircleTextLight />} title={t('Why recommended')}>
          <p className="text-gray-600 dark:text-gray-300 italic">{lawyer.reason}</p>
        </GlassCard>
      )}
      </div>
      {lawyer.specialty.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-200 mb-2">{t('Specializations')}</h3>
          <div className="flex flex-wrap gap-2">
            {lawyer.specialty.map((spec, index) => (
              <Badge key={index} color="indigo" className="text-sm">
                {spec}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderOfferTab = () => {
    if (loading) {
      return <p>{t('Loading offer...')}</p>;
    }
    if (!offer || !proposal) {
      return <p>{t('No offer received yet')}</p>;
    }
    const handleResponse = async (response: string, reason?: string) => {
      try {
        await apis.post('/matters/respond_to_offer', {
          LeadID: `${lawyer.lawyer_id}_${clientId}_${caseId}`,
          Response: response,
          Reason: reason,
        });
        dispatch(showFeedbackModal({
          showModal: true,
          modalType: 'success',
          message: t(`Offer ${response}ed successfully`),
          duration: 3000
        }));
        if (response === 'accept') {
          setProposal({ ...proposal, status: 'Accepted' });
          setOffer(null); 
        }
      } catch (error) {
        if(!error) return 
        dispatch(showFeedbackModal({
          showModal: true,
          modalType: 'error',
          message: t(`Failed to ${response} offer`),
          duration: 3000
        }));
      }
    };
    return (
      <>
        {proposal.status === 'Accepted' && <WaitingForEngagementTerms />}
        <OfferCard offer={offer} lawyer={lawyer} caseName={caseName} onRespond={handleResponse} status={proposal.status} />
      </>
    );
  };

  const renderProposalTab = () => {
    if (loading) {
      return <p>{t('Loading proposal...')}</p>;
    }
    if (proposal) {
      return (
        <>
          {proposal.status === 'Accepted' && <WaitingForEngagementTerms />}
          <ProposalCard proposal={proposal} lawyer={lawyer} caseName={caseName} />
        </>
      );
    }
    return <ProposalForm lawyerId={lawyer.lawyer_id} caseId={caseId} />;
  };

  const renderEngagementTermsTab = () => (
    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <p className="text-gray-600 dark:text-gray-300">
        {t('Engagement terms will be available once an agreement is made with the lawyer.')}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <Header />
      <div className="p-6">
        {renderProfile()}
        <FilterBarWithTabs tabs={tabs} activeTab={activeTab} onTabClick={goToTab} />
        <div className="mt-6">
          {activeTab === 'information' && renderInformationTab()}
          {activeTab === 'proposal' && renderProposalTab()}
          {activeTab === 'offer' && renderOfferTab()}
          {activeTab === 'engagement' && renderEngagementTermsTab()}
        </div>
      </div>
    </div>
  );
};

export default LawyerDetailsPage;